/**
 * Highcharts wrapper components styled to match the Pluma design system.
 *
 * Uses Highcharts styled mode — all colours come from CSS custom property
 * overrides in index.css.ts, so dark mode and Pluma theme changes work
 * automatically without any runtime CSS var resolution.
 *
 * Usage:
 *   import { Chart, Series, XAxis, YAxis } from '@/components/highcharts';
 *
 *   <Chart>
 *     <XAxis categories={['Jan', 'Feb', 'Mar']} />
 *     <Series type="spline" data={[1, 2, 3]} />
 *   </Chart>
 */
import {
  Chart as HCChart,
  Series,
  setHighcharts,
  type ICommonAttributes,
  type HighchartsOptionsType,
} from '@highcharts/react';
import { Title, Subtitle, Tooltip, Legend, XAxis, YAxis, PlotOptions } from '@highcharts/react';
import merge from 'lodash-es/merge';
import { useContext, createContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import * as styles from './index.css';
import { useMountEffect } from '@/hooks/use-mount-effect';

// ─── Async Highcharts loading ─────────────────────────────────────────────────
//
// Highcharts is a large library. Loading it asynchronously keeps it out of the
// main bundle and lets the rest of the app hydrate first (mirrors what the
// Ember UI does).

let _hcLoaded = false;
let _loadPromise: Promise<void> | null = null;
const _onLoadCallbacks: Array<() => void> = [];

/**
 * Triggers an async load of Highcharts. Safe to call multiple times — the
 * import only happens once. Can be awaited explicitly to pre-load Highcharts
 * (e.g. in a route loader), or awaited in tests to ensure initialisation is
 * complete before assertions.
 */
export function loadHighcharts(): Promise<void> {
  if (_loadPromise != null) return _loadPromise;
  _loadPromise = (async () => {
    // Load the main Highcharts bundle and CSS in parallel. Highcharts' UMD sets
    // window._Highcharts as a side effect — accessibility reads that global, so
    // it must finish executing *before* we import the accessibility module.
    const [hcModule] = await Promise.all([import('highcharts/highcharts'), import('highcharts/css/highcharts.css')]);

    // Now that window._Highcharts is set, load accessibility. Its UMD
    // self-executes on import — no manual init call needed.
    await import('highcharts/modules/accessibility');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hc = (hcModule as any).default ?? hcModule;
    setHighcharts(hc);
    _hcLoaded = true;
    for (const callback of _onLoadCallbacks.splice(0)) callback();
  })().catch((error) => {
    // Reset so callers can retry (e.g. on remount after a network failure).
    _loadPromise = null;
    throw error;
  });
  return _loadPromise;
}

function onHighchartsLoad(callback: () => void): () => void {
  if (_hcLoaded) {
    callback();
    return () => {};
  }
  _onLoadCallbacks.push(callback);
  return () => {
    const index = _onLoadCallbacks.indexOf(callback);
    if (index >= 0) _onLoadCallbacks.splice(index, 1);
  };
}

// ─── Options merge ────────────────────────────────────────────────────────────

// Deep-merge chart options objects. Uses lodash merge, which handles plain
// objects correctly (Date, RegExp, etc. are assigned, not recursed into) and
// avoids a synchronous Highcharts import just for Highcharts.merge.
function deepMerge<T extends object>(...sources: T[]): T {
  return merge({} as T, ...sources);
}

// ─── Default options ──────────────────────────────────────────────────────────

/**
 * Non-visual defaults shared by every chart. Visual styling lives in
 * index.css.ts via Highcharts CSS custom property overrides.
 */
const DEFAULT_OPTIONS: HighchartsOptionsType = {
  chart: {
    styledMode: true,
    animation: { duration: 400 },
    spacing: [24, 16, 16, 16],
  },
  title: { text: undefined },
  subtitle: { text: undefined },
  xAxis: {
    crosshair: { width: 1 },
  },
  yAxis: {
    title: { text: undefined },
  },
  legend: {
    align: 'left',
    verticalAlign: 'top',
  },
  tooltip: {
    shared: true,
    borderRadius: 6,
    padding: 15,
    // No inline style — font-size and font-weight are set via CSS on
    // .highcharts-tooltip .highcharts-header in index.css.ts.
    headerFormat: '{point.key}<br/>',
    shadow: false,
  },
  plotOptions: {
    series: {
      animation: { duration: 400 },
      marker: {
        enabled: false,
        symbol: 'circle',
        radius: 4,
        states: {
          hover: { enabled: true, radius: 4, lineWidth: 0 },
        },
      },
      states: {
        hover: { lineWidthPlus: 0 },
      },
    },
    column: { borderRadius: 4, borderWidth: 0 },
    bar: { borderRadius: 4, borderWidth: 0 },
    pie: {
      innerSize: '55%',
      borderWidth: 0,
      dataLabels: {
        style: { textOutline: 'none' },
      },
    },
  },
  credits: { enabled: false },
};

// ─── Context (optional override layer) ───────────────────────────────────────

const ChartsContext = createContext<HighchartsOptionsType>(DEFAULT_OPTIONS);

/**
 * Optional provider — wrap a group of charts to share a common options
 * baseline on top of the defaults (e.g. a shared height or custom colours).
 *
 * @note If `options` is defined inline (`<ChartsProvider options={{ ... }}>`),
 * the object reference changes on every parent render, causing all child charts
 * to re-merge options. Define `options` outside the component or via `useMemo`
 * at the call site if re-render performance matters.
 */
export function ChartsProvider({ children, options }: { children: ReactNode; options?: HighchartsOptionsType }) {
  const merged = useMemo(() => (options != null ? deepMerge(DEFAULT_OPTIONS, options) : DEFAULT_OPTIONS), [options]);

  return <ChartsContext.Provider value={merged}>{children}</ChartsContext.Provider>;
}

// ─── Chart ───────────────────────────────────────────────────────────────────

/**
 * A Highcharts chart pre-styled with Pluma design tokens via styled mode.
 * Renders a placeholder div until Highcharts has asynchronously initialised.
 *
 * @note If `options` is defined inline (`<Chart options={{ ... }} />`), the
 * object reference changes on every parent render, which defeats the `useMemo`
 * that merges options. Define `options` outside the component or via `useMemo`
 * at the call site if re-render performance matters.
 */
export function Chart({ options, containerProps, ...rest }: ICommonAttributes) {
  const base = useContext(ChartsContext);
  // Initialise synchronously if Highcharts already loaded (e.g. subsequent renders).
  const [ready, setReady] = useState(_hcLoaded);

  useMountEffect(() => {
    // If the module-level load failed (network error etc.), _loadPromise was reset to
    // null. Re-trigger it now so this mount gets a retry rather than waiting forever.
    if (!_hcLoaded && _loadPromise == null) {
      void loadHighcharts();
    }
    return onHighchartsLoad(() => setReady(true));
  });

  const mergedOptions = useMemo(
    // styledMode is pinned last so neither providers nor callers can disable it —
    // the entire CSS theming breaks without it.
    () => deepMerge(base, options ?? {}, { chart: { styledMode: true } } as HighchartsOptionsType),
    [base, options],
  );

  const wrapperClass = [styles.chartWrapper, containerProps?.className].filter(Boolean).join(' ');

  if (!ready) {
    // Pass through the caller's style so the placeholder reserves the same
    // space as the real chart — without this it collapses to 0 height.
    return <div className={wrapperClass} style={containerProps?.style} />;
  }

  return <HCChart options={mergedOptions} containerProps={{ ...containerProps, className: wrapperClass }} {...rest} />;
}

export { Series, Title, Subtitle, Tooltip, Legend, XAxis, YAxis, PlotOptions };
